/* RoomHistory.jsx — Immutable operational timeline for the Deal Room.
 *
 * Chronological, scannable, premium. Reads `/api/v2/deals/{id}/activity`
 * (already enriched with category + icon_key + actor_name + actor_role)
 * and renders Linear/Stripe/Notion-style activity cards grouped by day.
 *
 * Filters: documents · milestones · participants · risks · notes ·
 *          solicitor · financial · all
 *
 * Visual identity: ChainFlow OS light theme + glass cards. NO aggressive
 * red, NO social-feed vibes, NO emoji decoration. Each row carries:
 *   • per-category icon + soft tint
 *   • bold title + body + meta line (timestamp · actor · role · pill)
 *   • optional priority pill (Critical / Action) — subtle
 *
 * Day groupings render a sticky date header. Stalled periods between
 * activity bursts surface a quiet "N days quiet" divider — momentum
 * narration without alarm.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity as ActivityIcon,
  AlertOctagon,
  Calendar,
  ChevronRight,
  ClipboardCheck,
  Clock,
  FileText,
  Filter,
  GitBranch,
  Inbox,
  Info,
  Layers3,
  Mail,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { LIGHT } from "./RoomFlow";

/* Per-category icon registry — keyed by backend `icon_key`. */
const ICONS = {
  file:      FileText,
  milestone: GitBranch,
  users:     Users,
  mortgage:  Sparkles,
  chain:     GitBranch,
  alert:     AlertOctagon,
  calendar:  Calendar,
  clock:     Clock,
  task:      ClipboardCheck,
  message:   Mail,
  info:      Info,
};

/* Category → soft tint for icon background. Mirrors the
   notification-center palette so the operational identity is one
   continuous language. */
const TINT = {
  risk:        { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
  document:    { bg: "#EFF6FF", fg: "#1D4ED8", border: "#BFDBFE" },
  milestone:   { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
  participant: { bg: "#F5F3FF", fg: "#6D28D9", border: "#DDD6FE" },
  message:     { bg: "#F8FAFC", fg: "#475569", border: "#E2E8F0" },
  action:      { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
  mortgage:    { bg: "#F5F3FF", fg: "#6D28D9", border: "#DDD6FE" },
  chain:       { bg: "#FFFBEB", fg: "#A16207", border: "#FDE68A" },
  exchange:    { bg: "#F5F3FF", fg: "#6D28D9", border: "#DDD6FE" },
  inactivity:  { bg: "#F8FAFC", fg: "#64748B", border: "#E2E8F0" },
  system:      { bg: "#F8FAFC", fg: "#64748B", border: "#E2E8F0" },
};

/* Filter chip definitions — kept restrained. The "All" chip is implicit
   when no filters are active. */
const FILTERS = [
  { key: "document",    label: "Documents",   icon_key: "file" },
  { key: "milestone",   label: "Milestones",  icon_key: "milestone" },
  { key: "participant", label: "Participants",icon_key: "users" },
  { key: "risk",        label: "Risks",       icon_key: "alert" },
  { key: "message",     label: "Notes",       icon_key: "message" },
  { key: "mortgage",    label: "Mortgage",    icon_key: "mortgage" },
  { key: "exchange",    label: "Financial",   icon_key: "calendar" },
];

const fmtTimeOfDay = (ts) =>
  new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const fmtDateKey = (ts) => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const fmtDateHeader = (key) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(key);
  const diffDays = Math.round((today - d) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

const initialsOf = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const roleLabel = (role) => {
  if (!role) return null;
  const map = {
    admin:           "Admin",
    director:        "Director",
    manager:         "Manager",
    negotiator:      "Negotiator",
    sales_negotiator:"Negotiator",
    sales_progressor:"Progressor",
    buyer:           "Buyer",
    seller:          "Seller",
    buyer_solicitor: "Buyer Solicitor",
    seller_solicitor:"Seller Solicitor",
    solicitor:       "Solicitor",
    mortgage_broker: "Broker",
    broker:          "Broker",
  };
  return map[role] || role.replace(/_/g, " ");
};

const priorityPill = (priority) => {
  if (priority === "critical")
    return { label: "Critical", cls: "gp-pill gp-pill-rose" };
  if (priority === "important")
    return { label: "Action", cls: "gp-pill gp-pill-amber" };
  return null;
};

/* ─── Operational Intelligence Layer (Phase 4) ────────────────────────
   Renders the calm intelligence strip beneath an activity row when the
   backend has supplied an `intelligence_label` (i.e. the
   `timeline_intelligence_engine` had a blueprint for this event type).
   Calm typography · subtle severity dot · one-click action buttons.
   ────────────────────────────────────────────────────────────────── */
const SEVERITY_STYLE = {
  recovery: { fg: "#047857", bg: "rgba(16,185,129,0.10)",  dot: "#10B981" },
  info:     { fg: "#475569", bg: "rgba(148,163,184,0.10)", dot: "#94A3B8" },
  watch:    { fg: "#92400E", bg: "rgba(245,158,11,0.10)",  dot: "#F59E0B" },
  elevated: { fg: "#9F1239", bg: "rgba(244,63,94,0.08)",   dot: "#F43F5E" },
};

const MomentumChip = ({ delta }) => {
  if (!delta || delta === "stable") return null;
  const isImproved = delta === "improved";
  const Icon = isImproved ? TrendingUp : TrendingDown;
  const colour = isImproved ? "#047857" : "#9F1239";
  const bg = isImproved ? "rgba(16,185,129,0.10)" : "rgba(244,63,94,0.08)";
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-semibold"
      style={{ color: colour, background: bg }}
      data-testid={`history-momentum-${delta}`}
    >
      <Icon size={9} strokeWidth={2.4} />
      {isImproved ? "Momentum improved" : "Momentum softened"}
    </span>
  );
};

const ConfidenceChip = ({ pts }) => {
  if (pts == null) return null;
  const positive = pts > 0;
  const colour = positive ? "#047857" : pts < 0 ? "#9F1239" : "#475569";
  const bg = positive
    ? "rgba(16,185,129,0.10)"
    : pts < 0
    ? "rgba(244,63,94,0.08)"
    : "rgba(148,163,184,0.10)";
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-semibold tabular-nums"
      style={{ color: colour, background: bg }}
      data-testid={`history-confidence-${pts}`}
    >
      {positive ? "+" : ""}{pts} Confidence
    </span>
  );
};

const IntelligenceLayer = ({ row, dealId }) => {
  const [busyKey, setBusyKey] = useState(null);
  const sev = SEVERITY_STYLE[row.intelligence_severity] || SEVERITY_STYLE.info;
  const hasDownstream = !!row.downstream_effect;
  const hasActions = (row.recommended_actions || []).length > 0;
  const hasMeta = row.confidence_impact_pts != null
                  || (row.momentum_delta && row.momentum_delta !== "stable");

  const trigger = useCallback(async (actionKey) => {
    if (!dealId || !actionKey) return;
    setBusyKey(actionKey);
    try {
      const { data } = await api.post(`/api/deals/${dealId}/actions/${actionKey}`, {});
      toast.success(data?.confirmation || "Action recorded on the timeline.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
                || err?.response?.data?.detail
                || "Could not trigger action.";
      toast.error(typeof msg === "string" ? msg : "Could not trigger action.");
    } finally {
      setBusyKey(null);
    }
  }, [dealId]);

  return (
    <div
      className="mt-2 pt-2.5 pl-2 border-l-2 rounded-r-md"
      style={{ borderColor: sev.dot, background: sev.bg }}
      data-testid={`history-intelligence-${row.id}`}
    >
      <div className="px-2 pr-3 pb-2">
        <div className="flex items-center gap-1.5 mb-1">
          <span aria-hidden className="w-1 h-1 rounded-full" style={{ background: sev.dot }} />
          <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold" style={{ color: sev.fg }}>
            {row.intelligence_label || "Operational Watch"}
          </p>
        </div>
        {row.commentary ? (
          <p className="text-[12px] text-slate-700 leading-snug mb-2">
            {row.commentary}
          </p>
        ) : null}
        {hasDownstream ? (
          <div className="flex items-start gap-1.5 mb-2">
            <Layers3 size={11} className="mt-[2px] shrink-0" style={{ color: sev.fg }} strokeWidth={2.3} />
            <p className="text-[11.5px] text-slate-600 leading-snug">
              <span className="font-semibold text-slate-700">Downstream · </span>
              {row.downstream_effect}
            </p>
          </div>
        ) : null}
        {row.expected_turnaround ? (
          <p className="text-[10.5px] text-slate-500 leading-snug mb-2 tabular-nums">
            {row.expected_turnaround}
          </p>
        ) : null}
        {hasMeta ? (
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <ConfidenceChip pts={row.confidence_impact_pts} />
            <MomentumChip delta={row.momentum_delta} />
          </div>
        ) : null}
        {hasActions ? (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {row.recommended_actions.slice(0, 2).map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => trigger(a.key)}
                disabled={busyKey === a.key}
                className="inline-flex items-center gap-1 px-2 h-[24px] rounded-md text-[10.5px] font-semibold transition-all disabled:opacity-50"
                style={{
                  background: a.primary
                    ? "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(139,92,246,0.10))"
                    : "rgba(148,163,184,0.10)",
                  color: a.primary ? "#4338CA" : "#475569",
                  border: a.primary ? "1px solid rgba(99,102,241,0.22)" : "1px solid rgba(148,163,184,0.16)",
                }}
                data-testid={`history-action-${row.id}-${a.key}`}
              >
                <span>{a.label}</span>
                <ChevronRight size={10} strokeWidth={2.4} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ─── Activity row ────────────────────────────────────────────────── */
const ActivityRow = ({ row, dealId }) => {
  const Icon = ICONS[row.icon_key || "info"] || Info;
  const tint = TINT[row.category] || TINT.system;
  const pill = priorityPill(row.priority);
  const actor = row.actor_name;
  const rlabel = roleLabel(row.actor_role);
  return (
    <li
      className="relative pl-12 pr-4 sm:pr-5 py-4 group transition-colors hover:bg-violet-50/30"
      data-testid={`history-row-${row.id}`}
      data-category={row.category || "system"}
    >
      {/* Spine + icon node */}
      <span
        aria-hidden="true"
        className="absolute left-[18px] top-0 bottom-0 w-px"
        style={{ background: "rgba(15,23,42,0.07)" }}
      />
      <span
        className="absolute left-[8px] top-4 w-[22px] h-[22px] rounded-md flex items-center justify-center"
        style={{
          background: tint.bg,
          color: tint.fg,
          border: `1px solid ${tint.border}`,
        }}
      >
        <Icon size={11} />
      </span>
      <div className="flex items-start gap-2 flex-wrap">
        <p
          className="text-[13px] font-semibold leading-snug flex-1"
          style={{ color: LIGHT.textPrimary }}
        >
          {row.title}
        </p>
        {pill ? <span className={pill.cls}>{pill.label}</span> : null}
      </div>
      {row.description ? (
        <p
          className="mt-1 text-[12.5px] leading-snug"
          style={{ color: LIGHT.textMuted }}
        >
          {row.description}
        </p>
      ) : null}
      {row.intelligence_label ? (
        <IntelligenceLayer row={row} dealId={dealId} />
      ) : null}
      <div
        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
        style={{ color: LIGHT.textSubtle }}
      >
        <span className="tabular-nums">{fmtTimeOfDay(row.created_at)}</span>
        {actor ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[8.5px] font-bold text-white"
              style={{
                background: `hsl(${(actor.length * 31) % 360}, 55%, 50%)`,
              }}
            >
              {initialsOf(actor)}
            </span>
            <span style={{ color: LIGHT.textMuted, fontWeight: 600 }}>{actor}</span>
          </span>
        ) : (
          <span style={{ color: LIGHT.textSubtle }}>System</span>
        )}
        {rlabel ? (
          <span className="gp-pill gp-pill-slate">{rlabel}</span>
        ) : null}
        {row.category_label ? (
          <span style={{ color: LIGHT.textSubtle }}>· {row.category_label}</span>
        ) : null}
      </div>
    </li>
  );
};

/* ─── Day group ───────────────────────────────────────────────────── */
const DayGroup = ({ dateKey, rows, dealId }) => (
  <section data-testid={`history-day-${dateKey}`}>
    <header
      className="sticky top-0 z-10 px-5 sm:px-6 py-2 flex items-center gap-2"
      style={{
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${LIGHT.border}`,
      }}
    >
      <span
        className="text-[10.5px] uppercase tracking-[0.18em] font-bold"
        style={{ color: LIGHT.textSubtle }}
      >
        {fmtDateHeader(dateKey)}
      </span>
      <span
        className="text-[10.5px] font-semibold tabular-nums"
        style={{ color: LIGHT.textSubtle }}
      >
        · {rows.length}
      </span>
    </header>
    <ul className="divide-y" style={{ borderColor: LIGHT.border }}>
      {rows.map((r) => (
        <ActivityRow key={r.id} row={r} dealId={dealId} />
      ))}
    </ul>
  </section>
);

/* ─── Quiet stalled-period divider ────────────────────────────────── */
const StallDivider = ({ days }) => (
  <div
    className="px-5 sm:px-6 py-3 flex items-center gap-3"
    data-testid={`history-stall-${days}`}
  >
    <span
      className="flex-1 h-px"
      style={{ background: "rgba(15,23,42,0.08)" }}
    />
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold"
      style={{ color: LIGHT.textSubtle }}
    >
      <Clock size={11} /> {days === 1 ? "1 quiet day" : `${days} quiet days`}
    </span>
    <span
      className="flex-1 h-px"
      style={{ background: "rgba(15,23,42,0.08)" }}
    />
  </div>
);

/* ─── Filter bar ──────────────────────────────────────────────────── */
const FilterBar = ({ active, onToggle, totalCount }) => (
  <div
    className="px-5 sm:px-6 py-3 flex items-center gap-2 overflow-x-auto cf-scroll"
    style={{ borderBottom: `1px solid ${LIGHT.border}` }}
    data-testid="history-filter-bar"
  >
    <Filter size={12} style={{ color: LIGHT.textSubtle }} />
    {FILTERS.map((f) => {
      const isActive = active.includes(f.key);
      const Icon = ICONS[f.icon_key] || Info;
      return (
        <button
          key={f.key}
          type="button"
          onClick={() => onToggle(f.key)}
          data-testid={`history-filter-${f.key}`}
          className="shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold transition-all"
          style={
            isActive
              ? {
                  background: "var(--gp-violet-soft)",
                  color: "var(--gp-violet-deep)",
                  border: "1px solid var(--gp-violet-border)",
                }
              : {
                  background: "#FFFFFF",
                  color: LIGHT.textMuted,
                  border: `1px solid ${LIGHT.border}`,
                }
          }
        >
          <Icon size={10} /> {f.label}
        </button>
      );
    })}
    <span
      className="ml-auto text-[11px] tabular-nums shrink-0"
      style={{ color: LIGHT.textSubtle }}
    >
      {totalCount} {totalCount === 1 ? "event" : "events"}
    </span>
  </div>
);

/* ─── Main component ─────────────────────────────────────────────── */
export const RoomHistory = ({ dealId }) => {
  const [activeFilters, setActiveFilters] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (activeFilters.length > 0) {
          params.category = activeFilters.join(",");
        }
        const { data } = await api.get(`/api/v2/deals/${dealId}/activity`, { params });
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Could not load history.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, activeFilters]);

  const onToggleFilter = (key) =>
    setActiveFilters((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );

  /* Group rows by date (descending). Insert StallDivider between
     groups when ≥ 2 days of silence. */
  const { groups, totalCount } = useMemo(() => {
    const sorted = [...rows].sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at),
    );
    const byDate = new Map();
    for (const r of sorted) {
      const key = fmtDateKey(r.created_at);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(r);
    }
    return { groups: [...byDate.entries()], totalCount: sorted.length };
  }, [rows]);

  const items = useMemo(() => {
    const out = [];
    let prevKey = null;
    for (const [key, list] of groups) {
      if (prevKey) {
        const days = Math.round(
          (new Date(prevKey) - new Date(key)) / 86400000,
        );
        if (days >= 2) out.push({ type: "stall", days, key: `stall-${prevKey}-${key}` });
      }
      out.push({ type: "group", key, list });
      prevKey = key;
    }
    return out;
  }, [groups]);

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "#FFFFFF",
        border: `1px solid ${LIGHT.border}`,
        boxShadow: LIGHT.shadowSoft,
      }}
      data-testid="room-history"
    >
      <header className="px-5 sm:px-6 pt-5 pb-4 flex items-start justify-between gap-3">
        <div>
          <div className="gp-eyebrow flex items-center gap-2 mb-1">
            <ActivityIcon size={11} /> IMMUTABLE TIMELINE
          </div>
          <h2
            className="text-[16px] sm:text-[18px] font-semibold tracking-tight"
            style={{ color: LIGHT.textPrimary }}
          >
            Operational history
          </h2>
          <p
            className="mt-1 text-[12.5px]"
            style={{ color: LIGHT.textMuted }}
          >
            Every action, in order. Single source of truth for this transaction.
          </p>
        </div>
      </header>
      <FilterBar
        active={activeFilters}
        onToggle={onToggleFilter}
        totalCount={totalCount}
      />

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} />
      ) : rows.length === 0 ? (
        <EmptyState filtered={activeFilters.length > 0} />
      ) : (
        <div className="max-h-[68vh] overflow-y-auto cf-scroll">
          {items.map((it) =>
            it.type === "stall" ? (
              <StallDivider key={it.key} days={it.days} />
            ) : (
              <DayGroup key={it.key} dateKey={it.key} rows={it.list} dealId={dealId} />
            ),
          )}
        </div>
      )}
    </div>
  );
};

const Loading = () => (
  <div className="p-10 text-center" data-testid="history-loading">
    <p className="text-[12.5px]" style={{ color: LIGHT.textMuted }}>
      Loading operational history…
    </p>
  </div>
);

const ErrorState = ({ message }) => (
  <div
    className="m-5 sm:m-6 p-4 rounded-xl text-[12.5px]"
    style={{
      background: "var(--gp-rose-soft)",
      border: "1px solid var(--gp-rose-border)",
      color: "#991B1B",
    }}
    data-testid="history-error"
  >
    {message}
  </div>
);

const EmptyState = ({ filtered }) => (
  <div className="p-10 text-center" data-testid="history-empty">
    <div
      className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center"
      style={{
        background: "#F8FAFC",
        border: `1px solid ${LIGHT.border}`,
        color: LIGHT.textSubtle,
      }}
    >
      <Inbox size={18} />
    </div>
    <p className="mt-3 text-[13px] font-bold" style={{ color: LIGHT.textPrimary }}>
      {filtered ? "No events match these filters" : "No activity yet"}
    </p>
    <p className="mt-1 text-[12px]" style={{ color: LIGHT.textMuted }}>
      {filtered
        ? "Try removing a filter to widen the timeline."
        : "Every operational action on this transaction will land here."}
    </p>
  </div>
);
